import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { STORAGE_PRESETS, storageKeyFromForm, type StoragePresetId } from '@couch/shared';
import { useState, type SyntheticEvent } from 'react';
import { api, errorMessage } from '../../api/client';
import { cloudApi } from '../../api/cloud';
import { CopyField } from '../../components/CopyField';
import { CLOUD_KEYS_KEY } from './RunPodKeyPanel';

interface Fields { place: string; region: string; bucket: string; accessKeyId: string; secretAccessKey: string }
const BLANK: Fields = { place: '', region: '', bucket: '', accessKeyId: '', secretAccessKey: '' };

function Field({ id, label, value, onChange, secret }: { id: string; label: string; value: string; onChange: (v: string) => void; secret?: boolean }) {
  return (
    <>
      <label htmlFor={id}>{label}</label>
      <input id={id} type={secret ? 'password' : 'text'} autoComplete="off" spellCheck={false} value={value} onChange={(e) => { onChange(e.target.value); }} />
    </>
  );
}

/** Opt-in: the storage key on Couch, checked against the bucket, after the user confirmed what it can do (doc 73). */
function KeepOnCouch({ preset, onDone }: { preset: StoragePresetId; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [f, setF] = useState<Fields>(BLANK);
  const [understood, setUnderstood] = useState(false);
  const p = STORAGE_PRESETS[preset];
  const set = (name: keyof Fields) => (value: string) => { setF((old) => ({ ...old, [name]: value })); };
  const form = { preset, ...f, region: preset === 'other' ? f.region : undefined };
  const filled = f.bucket && f.accessKeyId && f.secretAccessKey && (!p.place || f.place);
  const key = filled ? storageKeyFromForm(form) : null;
  const save = useMutation({
    mutationFn: () => cloudApi.setStorageKey(form),
    onSuccess: async () => { setF(BLANK); onDone(); await queryClient.invalidateQueries({ queryKey: CLOUD_KEYS_KEY }); },
  });
  const submit = (e: SyntheticEvent) => { e.preventDefault(); save.mutate(); };
  return (
    <form className="runpod-key-form" onSubmit={submit}>
      {p.place && <Field id="storage-place" label={`${p.place.label} (${p.place.example})`} value={f.place} onChange={set('place')} />}
      {preset === 'other' && <Field id="storage-region" label="Region (as your provider names it)" value={f.region} onChange={set('region')} />}
      <Field id="storage-bucket" label="Bucket" value={f.bucket} onChange={set('bucket')} />
      <Field id="storage-key-id" label="Access key ID" value={f.accessKeyId} onChange={set('accessKeyId')} />
      <Field id="storage-secret" label="Secret access key" value={f.secretAccessKey} onChange={set('secretAccessKey')} secret />
      {key && 'problem' in key && <p role="alert" className="form-error">{key.problem}</p>}
      {key && !('problem' in key) && <p className="muted">Bucket {key.bucket} at {key.endpoint}.</p>}
      <label className="checkbox">
        <input type="checkbox" checked={understood} onChange={(e) => { setUnderstood(e.target.checked); }} />
        I understand: kept encrypted on Couch, this key could read and delete what is in this bucket if Couch were broken into.
      </label>
      {save.isError && <p role="alert" className="form-error">{errorMessage(save.error)}</p>}
      <button type="submit" disabled={!understood || !key || 'problem' in key || save.isPending}>{save.isPending ? 'Checking the bucket…' : 'Check and save on Couch'}</button>
    </form>
  );
}

/** Where the user's storage key is — never the key itself (doc 73). */
export function StorageKeyPanel({ preset }: { preset: StoragePresetId }) {
  const queryClient = useQueryClient();
  const [opting, setOpting] = useState(false);
  const keys = useQuery({ queryKey: CLOUD_KEYS_KEY, queryFn: api.cloudKeys });
  const remove = useMutation({ mutationFn: cloudApi.removeStorageKey, onSuccess: () => queryClient.invalidateQueries({ queryKey: CLOUD_KEYS_KEY }) });
  const holders = keys.data?.s3;
  const label = holders?.provider ? STORAGE_PRESETS[holders.provider].label : '';
  return (
    <div className="runpod-key">
      <h3>Your storage key</h3>
      {keys.isError && <p role="alert" className="form-error">{errorMessage(keys.error)}</p>}
      {holders?.machines.map((m) => <p key={m.id} className="success">{m.name} holds a key{m.online ? '' : ' (offline)'}.</p>)}
      <h4>On a home machine (recommended — Couch never sees the key)</h4>
      <p>In a terminal on that machine. It asks for the provider, the bucket and the key, and checks them:</p>
      <CopyField value="~/Couch/venv/bin/couch-agent cloud-key set s3" label="Keep the key on this Mac or Linux machine" />
      <CopyField value={'%USERPROFILE%\\Couch\\venv\\Scripts\\couch-agent.exe cloud-key set s3'} label="Keep the key on this Windows PC" />
      <p className="muted">Needs agent 0.13.0 or newer (Update agent on the machine card).</p>
      <h4>On Couch (opt-in)</h4>
      {holders?.server.set ? (
        <p>
          A key for bucket <strong>{holders.bucket}</strong> at {label} is kept on Couch, encrypted.{' '}
          <button type="button" className="button-quiet" disabled={remove.isPending} onClick={() => { remove.mutate(); }}>Remove the key from Couch</button>
        </p>
      ) : opting ? (
        <KeepOnCouch preset={preset} onDone={() => { setOpting(false); }} />
      ) : (
        <p>
          Then cloud machines can use the bucket when no home machine is on.{' '}
          <button type="button" className="button-quiet" onClick={() => { setOpting(true); }}>Keep my key on Couch…</button>
        </p>
      )}
    </div>
  );
}
